import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useFetch, pct, usd, fmtDate } from "@/lib/api";
import { PageHeader, Panel, Stat, Badge, Md, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const { data: m } = useFetch("/dashboard");
  const { data: br } = useFetch("/briefings");
  const { data: ex } = useFetch("/exceptions");
  if (!m) return <div className="text-zinc-500 font-mono text-sm caret">Loading outcomes</div>;
  const goal = m.goal_meetings || 0;
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Outcome dashboard" title="Qualified meetings, not activity." desc="Outcomes first, autonomy second. Opens and clicks are directional only.">
        <Link to="/manager"><Button data-testid="dash-run-manager-btn" className="bg-[#007AFF] hover:bg-[#3395FF]">Open AI Manager</Button></Link>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Stat testid="kpi-qualified-held" label="Qualified meetings held" value={`${m.qualified_meetings_held}/${goal}`} sub={`Booked ${m.meetings_booked} · Held ${m.meetings_held} · booked→held ${pct(m.booked_to_held)}`} tone="text-[#00C781]" />
        <Stat testid="kpi-accepted-opps" label="Sales-accepted opps" value={m.accepted_opportunities} sub={`Won revenue AUD ${m.won_revenue.toLocaleString()}`} />
        <Stat testid="kpi-cost-per-meeting" label="Cost / qualified meeting held" value={m.cost_per_qualified_meeting ? usd(m.cost_per_qualified_meeting) : "—"} sub={`Spend this month ${usd(m.spend_month_usd)} of $${m.budget_usd}`} />
        <Stat testid="kpi-autonomy" label="Autonomous completion" value={pct(m.autonomous_completion_rate)} sub={`Intervention ${pct(m.intervention_rate)} · Correction ${pct(m.correction_rate)}`} tone="text-[#3395FF]" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        <Stat testid="kpi-eligible" label="Eligible contacts" value={m.eligible_contacts} sub={`of ${m.contacts}`} />
        <Stat testid="kpi-delivered" label="Delivered" value={m.delivered} sub={`${m.really_sent} via live provider`} />
        <Stat testid="kpi-positive" label="Positive replies" value={m.positive_replies} sub={`reply→meeting ${pct(m.reply_to_meeting)}`} />
        <Stat testid="kpi-optouts" label="Opt-outs / complaints" value={`${m.opt_outs}/${m.complaints}`} tone={m.complaints ? "text-[#FF3B30]" : ""} />
        <Stat testid="kpi-exceptions" label="Open exceptions" value={m.exceptions_open} tone={m.exceptions_open ? "text-[#FFB020]" : ""} />
        <Stat testid="kpi-human-effort" label="Human effort" value={`${m.human_effort_minutes}m`} sub="logged review minutes" />
      </div>
      <div className="grid lg:grid-cols-3 gap-3">
        <Panel title="Delivery & replies · 14 days" className="lg:col-span-2">
          <div className="h-64" data-testid="activity-chart">
            <ResponsiveContainer>
              <AreaChart data={m.series}>
                <CartesianGrid stroke="#27272A" vertical={false} />
                <XAxis dataKey="day" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A", fontSize: 12 }} />
                <Area dataKey="sent" stroke="#007AFF" fill="#007AFF22" />
                <Area dataKey="replies" stroke="#00C781" fill="#00C78122" />
                <Area dataKey="blocked" stroke="#FF3B30" fill="#FF3B3011" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Decisions waiting on you" action={<Link to="/exceptions" className="text-xs text-[#3395FF] hover:underline" data-testid="dash-view-exceptions">View all</Link>}>
          {!ex?.length ? <Empty>Nothing needs your judgement.</Empty> : (
            <ul className="space-y-3">{ex.slice(0, 6).map((e) => (
              <li key={e.id} className="flex items-start gap-2 text-sm"><Badge v={e.severity} /><span className="text-zinc-300">{e.title}</span></li>
            ))}</ul>
          )}
        </Panel>
        <Panel title="Latest management briefing" className="lg:col-span-3" action={<span className="text-xs text-zinc-500 font-mono">{fmtDate(br?.[0]?.created_at)}</span>}>
          {br?.[0] ? <div data-testid="latest-briefing"><Md text={br[0].text} /></div> : <Empty>Run the AI Manager to generate the first briefing.</Empty>}
        </Panel>
      </div>
    </div>
  );
}
